"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { Reveal } from "@/components/motion/reveal";
import { SpotlightCard } from "@/components/aceternity/spotlight-card";
import { Button } from "@/components/ui/button";
import { EmailMenu } from "@/components/contact-actions";

/**
 * Pricing. Three tiers anchored to the existing KES structure. A monthly/annual
 * segmented control cross-fades the price block (dissolve out → emerge in) using
 * AnimatePresence, matching the emerge/dissolve motion language of the rest of
 * the page. Annual bills two months free.
 */

type Cycle = "monthly" | "annual";

type Tier = {
  name: string;
  blurb: string;
  monthly: string;
  annual: string;
  unit: string;
  features: string[];
  cta: string;
  featured?: boolean;
};

const TIERS: Tier[] = [
  {
    name: "Cyber Café",
    blurb: "Shared machines, tight margins, full protection.",
    monthly: "200–250",
    annual: "2,000–2,500",
    unit: "KES / device / mo",
    features: [
      "Up to 20 devices",
      "5-layer detection",
      "Trust-based network isolation",
      "Plain-English alerts",
    ],
    cta: "Start protecting",
  },
  {
    name: "SME",
    blurb: "Growing teams that need a real SOC without the headcount.",
    monthly: "500–670",
    annual: "5,000–6,700",
    unit: "KES / device / mo",
    features: [
      "Up to 200 devices",
      "Everything in Cyber Café",
      "Auto Mode playbooks",
      "AI threat analyst + hunting",
      "Compliance reporting",
    ],
    cta: "Choose SME",
    featured: true,
  },
  {
    name: "Enterprise",
    blurb: "Fleet-scale deployments with your own controls.",
    monthly: "Custom",
    annual: "Custom",
    unit: "talk to us",
    features: [
      "Unlimited devices",
      "Everything in SME",
      "Dedicated onboarding",
      "SLA + priority support",
      "On-prem / air-gapped options",
    ],
    cta: "Contact sales",
  },
];

export function Pricing() {
  const [cycle, setCycle] = useState<Cycle>("monthly");

  return (
    <section id="pricing" className="relative mx-auto w-full max-w-6xl px-6 py-24">
      <Reveal className="relative">
        <div className="text-center">
          <span className="mono-label text-accent-text">pricing</span>
          <h2 className="mx-auto mt-3 max-w-2xl text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Priced for a cyber café. Built for an enterprise.
          </h2>

          {/* Billing cycle switch */}
          <div className="mt-8 inline-flex gap-1 rounded-xl2 p-1.5" style={{ background: "color-mix(in srgb, var(--accent) 8%, transparent)" }}>
            {(["monthly", "annual"] as Cycle[]).map((c) => (
              <motion.button
                key={c}
                onClick={() => setCycle(c)}
                whileTap={{ scale: 0.95 }}
                className="relative min-h-[44px] rounded-xl2 px-5 py-2 text-base font-semibold capitalize"
                style={cycle === c ? { color: "var(--text)" } : { color: "var(--muted)" }}
              >
                {cycle === c && (
                  <motion.span
                    layoutId="cycle-pill"
                    className="glass neo absolute inset-0 rounded-xl2"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative">{c === "annual" ? "Annual · 2 mo free" : "Monthly"}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {TIERS.map((t, i) => (
          <Reveal key={t.name} index={i}>
            <SpotlightCard
              index={i}
              className={`flex h-full flex-col rounded-xl2 p-8 ${t.featured ? "outline outline-2 outline-[var(--accent)]" : ""}`}
            >
              {t.featured && (
                <span className="mono-label mb-4 inline-block w-fit rounded-full bg-accent px-3 py-1 text-accent-ink">
                  most popular
                </span>
              )}
              <h3 className="text-xl font-bold tracking-tight">{t.name}</h3>
              <p className="mt-2 text-base text-muted">{t.blurb}</p>

              {/* Cross-fading price */}
              <div className="mt-6 min-h-[74px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={cycle + t.name}
                    initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-3xl font-bold tabular-nums">
                        {cycle === "monthly" ? t.monthly : t.annual}
                      </span>
                    </div>
                    <div className="mono-label mt-1 text-muted">
                      {cycle === "annual" && t.unit.includes("mo")
                        ? "KES / device / yr"
                        : t.unit}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <ul className="mt-6 space-y-2.5 text-base">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-muted">
                    <span aria-hidden className="mt-0.5 text-accent-text">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-2">
                {t.name === "Enterprise" ? (
                  <EmailMenu
                    subject="Enterprise enquiry"
                    wrapClassName="relative block w-full"
                    renderTrigger={({ toggle }) => (
                      <Button
                        onClick={toggle}
                        variant={t.featured ? "primary" : "ghost"}
                        className="w-full"
                      >
                        {t.cta}
                      </Button>
                    )}
                  />
                ) : (
                  <Button
                    href="#download"
                    variant={t.featured ? "primary" : "ghost"}
                    className="w-full"
                  >
                    {t.cta}
                  </Button>
                )}
              </div>
            </SpotlightCard>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
